import './App.css'
import React from 'react'
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from 'react-router-dom'
import {
  createBrowserRouter,
  RouterProvider,
  ScrollRestoration,
} from 'react-router-dom'
import { Home } from './pages/Home'
import { Story } from './pages/Story'
import { About } from './pages/About'
import { Print } from './pages/Print'
import { StaticPage } from './pages/StaticPage'
import { Archive } from './pages/Archive'
import { StoryKloster } from './pages/StoryKloster'
import client from './client.js'
import imageUrlBuilder from '@sanity/image-url'

const devYears = []
const dynamicYears = [2020, 2022, 2023, 2024, 2025]
const staticYears = [2016, 2017, 2018, 2019]
const activeYear = 2025
const videoStories = [
  '/2023/compare',
  '2024/championships-are-won-in-the-preparation',
]

const builder = imageUrlBuilder(client)

function urlFor(source) {
  return builder.image(source)
}

function RootLayout() {
  const isFirstNavigation = React.useRef(true) // Track first navigation

  return (
    <>
      <Outlet />
      <ScrollRestoration
        getKey={(location) => {
          console.log('ScrollRestoration getKey called:', location.pathname)

          if (isFirstNavigation.current) {
            console.log(
              'First navigation detected, skipping scroll restoration'
            )
            isFirstNavigation.current = false // Mark first navigation as complete
            return null // Disable scroll restoration for the first navigation
          }

          return location.pathname // Enable scroll restoration for subsequent navigations
        }}
      />
    </>
  )
}

async function storyLoader({ params }) {
  console.log('storyLoader called for slug:', params.slug) // Log the slug being loaded
  const path = window.location.pathname
  const prefetchCache = window.__PREFETCH_CACHE__ || new Map()

  // Try to get prefetched data
  const prefetchedData = prefetchCache.get(path)
  if (prefetchedData) {
    console.log('Using prefetched data for path:', path) // Log prefetch usage
    prefetchCache.delete(path)
    return prefetchedData
  }

  console.log('Fetching data for slug:', params.slug) // Log when fetching new data
  const data = await client.fetch(`
    *[_type == "story" && slug.current == "${params.slug}"][0]{
      title,
      slug,
      mainImage {
        ..., // this will ensure you keep the existing data
        ...asset-> {
          altText,
          caption,
          ...metadata {
            lqip, // the lqip can be used for blurHashUrl or other low-quality placeholders
            ...dimensions {
              width,
              height
            }
          }
        }
      },
      "mainImageHotSpot": mainImage.hotspot,
      cardImage,
      body,
      firstByline,
      secondByline,
      seoDescription,
      timeToRead,
      year,
      "authors": authors[]->{name, slug, image, bio},
      "relatedStories": relatedStories[]->{
        title,
        slug,
        mainImage,
        cardImage,
        firstByline
      }
    }
  `)

  if (!data) {
    console.error('Story not found for slug:', params.slug) // Log if story is not found
    throw new Error('Story not found')
  }

  console.log('Data fetched for slug:', params.slug, data) // Log fetched data
  return data
}

async function homeLoader({ params }) {
  // just get the current year from the url
  const year = window.location.pathname.split('/')[1]
  console.log('homeLoader called for year:', year) // Corrected to use year
  const path = window.location.pathname
  const prefetchCache = window.__PREFETCH_CACHE__ || new Map()

  // Try to get prefetched data
  const prefetchedData = prefetchCache.get(path)
  if (prefetchedData) {
    console.log('Using prefetched data for path:', path) // Log prefetch usage
    prefetchCache.delete(path)
    return prefetchedData
  }

  console.log('Fetching data for year:', year) // Log when fetching new data
  const data = await client.fetch(`
    *[_type == "story" && year == "${year}"] | order(order asc){
      title,
      slug,
      mainImage {
        ..., // this will ensure you keep the existing data
        ...asset-> {
          altText,
          caption,
          ...metadata {
            lqip, // the lqip can be used for blurHashUrl or other low-quality placeholders
            ...dimensions {
              width,
              height
            }
          }
        }
      },
      "mainImageHotSpot": mainImage.hotspot,
      cardImage {
        ..., // this will ensure you keep the existing data
        ...asset-> {
          altText,
          caption,
          ...metadata {
            lqip, // the lqip can be used for blurHashUrl or other low-quality placeholders
            ...dimensions {
              width,
              height
            }
          }
        }
      },
      firstByline,
      iconColor,
      seoDescription,
      timeToRead,
    }
  `)

  if (!data) {
    console.error('Data not found for year:', year) // Log if data is not found
    throw new Error('Data not found')
  }

  console.log('Data fetched for year:', year, data) // Log fetched data
  return data
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      ...staticYears.flatMap((year) => [
        {
          path: `/${year}`,
          element: <StaticPage year={year} />,
        },
        {
          path: `/${year}/:slug`,
          element: <StaticPage year={year} />,
        },
      ]),
      ...dynamicYears.flatMap((year) => [
        {
          path: `/${year}`,
          element: <Home year={year} />,
          loader: homeLoader,
        },
        {
          path: `/${year}/about`,
          element: <About year={year} />,
        },
        {
          path: `/${year}/print`,
          element: <Print year={year} />,
        },
        {
          path: `/${year}/:slug`,
          element: <Story year={year} />,
          loader: storyLoader,
        },
      ]),
      ...devYears.flatMap((year) => [
        {
          path: `/${year}-dev`,
          element: <Home year={year} />,
          loader: homeLoader,
        },
        {
          path: `/${year}-dev/about`,
          element: <About year={year} />,
        },
        {
          path: `/${year}-dev/print`,
          element: <Print year={year} />,
        },
        {
          path: `/${year}-dev/:slug`,
          element: <Story year={year} />,
          loader: storyLoader,
        },
      ]),

      {
        path: '/',
        element: <Navigate to={`/${activeYear}`} replace />,
      },
      {
        path: '/about',
        element: <About year={activeYear} />,
      },
      {
        path: '/archive',
        element: <Archive year={activeYear} />,
      },
      {
        path: '/print',
        element: <Print year={activeYear} />,
      },
      {
        path: '*',
        element: <Navigate to={`/${activeYear}`} replace />,
      },
    ],
  },
])

function App() {
  return <RouterProvider router={router} />
}

export default App
